import { playlistActionTypes } from '@/playlist/Constants';
import { exerciseTypeCodesNext } from '@/services/Constants';
import { getScoreData } from '@/playlist/services/Helper';

import type { PlaylistAction, PlaylistState } from '@/playlist/types';

// ============================================================================
// Initial State (Flattened for backward compatibility with exerciseReducer)
// ============================================================================

const initialGameSubState = {
	type: null,
	totalTimeInSeconds: 0,
	currentLevel: null,
	highScore: null,
	rewardAttribute: null,
	correctAnswerStreak: 0,
	reward: null,
};

const initialState: PlaylistState = {
	// Core playlist data
	id: null,
	playList: null,
	questions: [],
	loading: false,
	loadingAnswer: false,
	loadingFinish: false,

	// Scoring
	correctCount: 0,
	incorrectCount: 0,
	questionsLeftCount: 0,
	correctStreak: 0,

	// Current question
	currentQuestion: null,

	// Metadata
	datePlayed: null,
	sectionType: null,
	topicTitle: null,
	topicSubcategoryName: null,
	playlistSectionText: { title: null, text: null },
	freePlayTheme: null,
	viewMode: null,
	skipAnswerRequests: false,
	skipFinishRequest: false,
	isBridgePlaylist: false,
	isPersonalityQuiz: false,
	personalityQuizAnswerIds: [] as number[],

	// Game-specific (nested)
	game: initialGameSubState,

	// Exercise-specific UI (flattened for backward compatibility)
	audioOn: true,
	instructionAudioOn: false,
	isReadingMode: false,
	readingProgress: 0,
	readingTextSize: 1,
	showTopicInstruction: false,
	showQuestionInstruction: false,
	showInstructionHint: false,
	showPulsatingHighlight: false,
	showReadingTitle: false,
	showAITutorChat: false,
	aiTutorFeedbackOpen: false,
	aiTutorFeedbacks: {},
	instructionFeedbacks: {},
	questionsExplanations: {},
	exerciseTypes: {
		isDictateType: false,
		isOpenEndedType: false,
		isInYourMindType: false,
		isMultipleChoiceType: false,
		isTheBlanksType: false,
		isOpenMultipleType: false,
		isDictateMultipleType: false,
		isMultipleChoiceMultipleAnswersType: false,
	},
	instruction: null,

	// Completion
	endscreen: null,
	levelUp: null,
	mistakeInterventionId: null,
	loadingMistakeIntervention: false,

	// Legacy snake_case aliases for backward compatibility
	section_type: null,
	topic_title: null,
	topic_subcategory_name: null,
	playlist_section_text: { title: null, text: null },
	is_mistake: false,
	speech_locale: null,
};

// ============================================================================
// Reducer
// ============================================================================

export default function playlistReducer(
	state: PlaylistState = initialState,
	action: PlaylistAction,
): PlaylistState {
	const { type, payload } = action;
	const {
		audioOn,
		correctCount,
		correctStreak,
		currentQuestion,
		currentLevel,
		data,
		endscreen,
		exerciseType,
		incorrectCount,
		instructionAudioOn,
		instructionId,
		isReadingMode,
		isUseful,
		levelUp,
		questionId,
		questionsLeftCount,
		readingProgress,
		readingTextSize,
		showInstructionHint,
		showPulsatingHighlight,
		showQuestionInstruction,
		showReadingTitle,
		showTopicInstruction,
		mistakeInterventionId,
		viewMode,
		skipAnswerRequests,
		skipFinishRequest,
		isBridgePlaylist,
		isPersonalityQuiz,
		answerId,
		aiTutorFeedbackOpen,
		feedback,
		playList,
	} = payload || {};

	switch (type) {
		// ====================================================================
		// Loading States
		// ====================================================================
		case playlistActionTypes.GET_QUESTIONS_EXPLANATIONS:
		case playlistActionTypes.GET_PLAYLIST_REQUEST:
		case playlistActionTypes.GET_PLAYLIST_REPORT_REQUEST:
		case playlistActionTypes.GET_PLAYLIST_REPLAY_REQUEST:
			return {
				...state,
				loading: true,
			};

		case playlistActionTypes.SEND_ANSWER_REQUEST:
			return {
				...state,
				loadingAnswer: true,
			};

		case playlistActionTypes.SEND_ANSWER_SUCCESS:
		case playlistActionTypes.SEND_ANSWER_ERROR:
			return {
				...state,
				loadingAnswer: false,
			};

		case playlistActionTypes.FINISH_PLAYLIST_REQUEST:
			return {
				...state,
				loadingFinish: true,
			};

		case playlistActionTypes.FINISH_PLAYLIST_SUCCESS:
		case playlistActionTypes.FINISH_PLAYLIST_ERROR:
			return {
				...state,
				loadingFinish: false,
			};

		// ====================================================================
		// Playlist Fetching Success
		// ====================================================================
		case playlistActionTypes.GET_QUESTIONS_EXPLANATIONS_SUCCESS:
			return {
				...state,
				questionsExplanations: { ...state.questionsExplanations, ...data },
			};

		case playlistActionTypes.GET_PLAYLIST_SUCCESS: {
			const playlistData = data || playList;
			const { correct, incorrect, questionsLeft } = getScoreData(playlistData);

			// Handle game data if present
			const gameConfig = playlistData?.game;
			const gameSubState = gameConfig
				? {
						type: gameConfig.type || null,
						totalTimeInSeconds: gameConfig.total_time_in_seconds || 0,
						highScore: gameConfig.high_score || null,
						rewardAttribute: gameConfig.reward_attribute || null,
						correctAnswerStreak: gameConfig.correct_answer_streak || 0,
						reward: gameConfig.reward || null,
						currentLevel: state.game.currentLevel,
				  }
				: state.game;

			return {
				...state,
				loading: false,
				skipAnswerRequests: false,
				skipFinishRequest: false,
				isPersonalityQuiz: false,
				id: playlistData?.id ?? state.id,
				playList: playlistData,
				questions: playlistData?.questions || [],
				correctCount: correct,
				incorrectCount: incorrect,
				questionsLeftCount: questionsLeft.length,
				freePlayTheme: playlistData?.free_play_theme || null,
				sectionType: playlistData?.section_type || null,
				section_type: playlistData?.section_type || null,
				topicTitle: playlistData?.topic_title || null,
				topic_title: playlistData?.topic_title || null,
				topicSubcategoryName: playlistData?.topic_subcategory_name || null,
				topic_subcategory_name: playlistData?.topic_subcategory_name || null,
				playlistSectionText: playlistData?.playlist_section_text || {
					title: null,
					text: null,
				},
				instruction: playlistData?.instruction || null,
				playlist_section_text: playlistData?.playlist_section_text || {
					title: null,
					text: null,
				},
				is_mistake: playlistData?.is_mistake || false,
				speech_locale: playlistData?.speech_locale || null,
				game: gameSubState,
			};
		}

		case playlistActionTypes.GET_PLAYLIST_REPORT_SUCCESS:
			return {
				...state,
				loading: false,
				skipAnswerRequests: true,
				skipFinishRequest: true,
				id: data?.id ?? state.id,
				playList: data,
				questions: data?.questions || [],
				correctCount: data?.correct_answers_count || 0,
				incorrectCount: data?.wrong_answers_count || 0,
				datePlayed: data?.date_played || null,
				sectionType: data?.section_type || null,
				section_type: data?.section_type || null,
				topicTitle: data?.topic_title || null,
				topic_title: data?.topic_title || null,
				topicSubcategoryName: data?.topic_subcategory_name || null,
				topic_subcategory_name: data?.topic_subcategory_name || null,
				playlistSectionText: data?.playlist_section_text || { title: null, text: null },
				instruction: data?.instruction || null,
				playlist_section_text: data?.playlist_section_text || { title: null, text: null },
			};

		case playlistActionTypes.GET_PLAYLIST_REPLAY_SUCCESS:
			return {
				...state,
				loading: false,
				skipAnswerRequests: true,
				skipFinishRequest: true,
				id: data?.id ?? state.id,
				playList: data,
				questions: data?.questions || [],
				correctCount: data?.correct_answers_count || 0,
				incorrectCount: data?.wrong_answers_count || 0,
				datePlayed: data?.date_played || null,
				sectionType: data?.section_type || null,
				section_type: data?.section_type || null,
				topicTitle: data?.topic_title || null,
				topic_title: data?.topic_title || null,
				topicSubcategoryName: data?.topic_subcategory_name || null,
				topic_subcategory_name: data?.topic_subcategory_name || null,
				playlistSectionText: data?.playlist_section_text || { title: null, text: null },
				instruction: data?.instruction || null,
				playlist_section_text: data?.playlist_section_text || { title: null, text: null },
			};

		case playlistActionTypes.GET_PLAYLIST_ERROR:
		case playlistActionTypes.GET_PLAYLIST_REPORT_ERROR:
		case playlistActionTypes.GET_PLAYLIST_REPLAY_ERROR:
			return {
				...state,
				loading: false,
			};

		// ====================================================================
		// State Management
		// ====================================================================
		case playlistActionTypes.CLEAR_STATE: {
			const preservedViewMode = state.viewMode;
			const preservedExplanations = state.questionsExplanations;
			return {
				...initialState,
				viewMode: preservedViewMode,
				questionsExplanations: preservedExplanations,
			};
		}

		case playlistActionTypes.SET_EXERCISE_SCORE:
			return {
				...state,
				correctCount: correctCount ?? state.correctCount,
				incorrectCount: incorrectCount ?? state.incorrectCount,
				questionsLeftCount: questionsLeftCount ?? state.questionsLeftCount,
			};

		case playlistActionTypes.SET_CURRENT_QUESTION:
			return {
				...state,
				currentQuestion: currentQuestion ?? null,
			};

		case playlistActionTypes.SET_VIEW_MODE:
			return {
				...state,
				viewMode: (viewMode as PlaylistState['viewMode']) ?? null,
			};

		case playlistActionTypes.SET_SKIP_ANSWER_REQUESTS:
			return {
				...state,
				skipAnswerRequests: !!skipAnswerRequests,
			};

		case playlistActionTypes.SET_SKIP_FINISH_REQUEST:
			return {
				...state,
				skipFinishRequest: !!skipFinishRequest,
			};

		case playlistActionTypes.SET_IS_BRIDGE_PLAYLIST:
			return {
				...state,
				isBridgePlaylist: !!isBridgePlaylist,
			};

		case playlistActionTypes.SET_IS_PERSONALITY_QUIZ:
			return {
				...state,
				isPersonalityQuiz: !!isPersonalityQuiz,
				personalityQuizAnswerIds: [],
			};

		case playlistActionTypes.ADD_PERSONALITY_QUIZ_ANSWER_ID:
			return {
				...state,
				personalityQuizAnswerIds: [...state.personalityQuizAnswerIds, answerId as number],
			};

		// ====================================================================
		// Exercise Types
		// ====================================================================
		case playlistActionTypes.HANDLE_NEXT_EXERCISE_TYPES:
			return {
				...state,
				exerciseTypes: {
					isDictateType: exerciseTypeCodesNext.DICTATE === exerciseType,
					isOpenEndedType: exerciseTypeCodesNext.OPEN_ENDED === exerciseType,
					isInYourMindType: exerciseTypeCodesNext.IN_YOUR_MIND === exerciseType,
					isMultipleChoiceType: exerciseTypeCodesNext.MULTIPLE_CHOICE === exerciseType,
					isTheBlanksType: exerciseTypeCodesNext.THE_BLANKS === exerciseType,
					isOpenMultipleType: exerciseTypeCodesNext.OPEN_MULTIPLE === exerciseType,
					isDictateMultipleType: exerciseTypeCodesNext.DICTATE_MULTIPLE === exerciseType,
					isMultipleChoiceMultipleAnswersType:
						exerciseTypeCodesNext.MULTIPLE_CHOICE_MULTIPLE_ANSWERS === exerciseType,
				},
			};

		// ====================================================================
		// Scoring
		// ====================================================================
		case playlistActionTypes.SET_CORRECT_STREAK:
			return {
				...state,
				correctStreak: correctStreak ?? state.correctStreak,
			};

		// ====================================================================
		// Instructions
		// ====================================================================
		case playlistActionTypes.TOGGLE_TOPIC_INSTRUCTION:
			return {
				...state,
				showTopicInstruction: !!showTopicInstruction,
			};

		case playlistActionTypes.TOGGLE_QUESTION_INSTRUCTION:
			return {
				...state,
				showQuestionInstruction: !!showQuestionInstruction,
			};

		case playlistActionTypes.SET_SHOW_INSTRUCTION_HINT:
			return {
				...state,
				showInstructionHint: !!showInstructionHint,
			};

		case playlistActionTypes.SEND_TOPIC_INSTRUCTION_FEEDBACK:
			return {
				...state,
				instructionFeedbacks: {
					...state.instructionFeedbacks,
					[`${instructionId}_${questionId}`]: isUseful ?? false,
				},
			};

		// ====================================================================
		// Audio
		// ====================================================================
		case playlistActionTypes.TOGGLE_INSTRUCTION_AUDIO:
			return {
				...state,
				audioOn: !!audioOn,
			};

		case playlistActionTypes.SET_INSTRUCTION_AUDIO_ON:
			return {
				...state,
				instructionAudioOn: !!instructionAudioOn,
			};

		// ====================================================================
		// Reading Mode
		// ====================================================================
		case playlistActionTypes.TOGGLE_READING_MODE:
			return {
				...state,
				isReadingMode: !!isReadingMode,
			};

		case playlistActionTypes.TOGGLE_READING_TITLE:
			return {
				...state,
				showReadingTitle: !!showReadingTitle,
			};

		case playlistActionTypes.TOGGLE_PULSATING_HIGHLIGHT:
			return {
				...state,
				showPulsatingHighlight: !!showPulsatingHighlight,
			};

		case playlistActionTypes.SET_READING_PROGRESS:
			return {
				...state,
				readingProgress: readingProgress ?? state.readingProgress,
			};

		case playlistActionTypes.SET_READING_TEXT_SIZE:
			return {
				...state,
				readingTextSize: readingTextSize ?? state.readingTextSize,
			};

		// ====================================================================
		// Completion States
		// ====================================================================
		case playlistActionTypes.SET_ENDSCREEN:
			return {
				...state,
				endscreen,
			};

		case playlistActionTypes.SET_LEVEL_UP:
			return {
				...state,
				levelUp,
			};

		// ====================================================================
		// Mistake Intervention
		// ====================================================================
		case playlistActionTypes.SET_MISTAKE_INTERVENTION:
			return {
				...state,
				mistakeInterventionId: mistakeInterventionId ?? null,
			};

		case playlistActionTypes.SEND_MISTAKE_INTERVENTION_REQUEST:
			return {
				...state,
				loadingMistakeIntervention: true,
			};

		case playlistActionTypes.SEND_MISTAKE_INTERVENTION_SUCCESS:
		case playlistActionTypes.SEND_MISTAKE_INTERVENTION_ERROR:
			return {
				...state,
				loadingMistakeIntervention: false,
			};

		// ====================================================================
		// AI Tutor
		// ====================================================================
		case playlistActionTypes.SEND_AI_TUTOR_FEEDBACK:
			return {
				...state,
				aiTutorFeedbacks: {
					...state.aiTutorFeedbacks,
					[`${questionId}`]: feedback,
				},
			};

		case playlistActionTypes.TOGGLE_AI_TUTOR_FEEDBACK:
			return {
				...state,
				aiTutorFeedbackOpen: !!aiTutorFeedbackOpen,
			};

		// ====================================================================
		// Game-specific
		// ====================================================================
		case playlistActionTypes.SET_CURRENT_LEVEL:
			return {
				...state,
				game: {
					...state.game,
					currentLevel: currentLevel ?? null,
				},
			};

		default:
			return state;
	}
}
